/**
 * Right-clicking a STAND on the bench (its tripod, rod, or rack — not the
 * glassware standing in it) asks what to put on it.
 *
 * The menu is built for the stand it belongs to rather than being one menu for
 * everything: a rack offers "Add Test Tube" as a plain action, since a tube is
 * the only thing it holds and a one-icon picker row would be silly; and a stand
 * no flame can reach shows no burner section at all, only the reason. The
 * container menu is the mirror of this for the glassware itself
 * (ContainerContextMenu).
 */
import { useRef } from 'react';
import { VESSEL_LABELS, type VesselType } from '../vesselTypes';
import { STAND_LABELS, type StandType } from '../standTypes';
import { BURNER_TYPES, BURNER_HINTS, BURNER_LABELS, type BurnerType } from '../burnerTypes';
import { BurnerIcon, VesselGlyph } from './ApparatusIcons';
import { useDismiss, useAnchoredPosition } from './contextMenu';

interface StandContextMenuProps {
  x: number;
  y: number;
  standType: StandType;
  /** The glassware this stand can hold, empty when it's already full. */
  vesselOptions: VesselType[];
  /** Why no container can be added, when none can. */
  fullReason?: string;
  onAddContainer: (type: VesselType) => void;
  burnerType: BurnerType;
  /** False for a stand no bench-standing flame can reach. */
  canHeat: boolean;
  onChangeBurner: (burner: BurnerType) => void;
  onClose: () => void;
}

export function StandContextMenu({
  x, y, standType, vesselOptions, fullReason, onAddContainer,
  burnerType, canHeat, onChangeBurner, onClose,
}: StandContextMenuProps): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, onClose);
  const pos = useAnchoredPosition(ref, x, y);

  return (
    <div ref={ref} className="chemlab-context-menu" style={pos}>
      <p className="chemlab-context-menu-title">{STAND_LABELS[standType]}</p>

      {/* One kind of glassware → a plain action. Several → a picker. */}
      {vesselOptions.length === 1 && (
        <button
          className="chemlab-context-menu-item"
          onClick={() => { onAddContainer(vesselOptions[0]); onClose(); }}
        >
          <VesselGlyph vessel={vesselOptions[0]} size={16} />
          <span>Add {VESSEL_LABELS[vesselOptions[0]]}</span>
        </button>
      )}
      {vesselOptions.length > 1 && (
        <>
          <p className="chemlab-context-menu-hint">Add Container</p>
          <div className="chemlab-context-menu-vessels">
            {vesselOptions.map(type => (
              <button
                key={type}
                className="chemlab-context-menu-vessel"
                title={`Stand a ${VESSEL_LABELS[type].toLowerCase()} on this`}
                onClick={() => { onAddContainer(type); onClose(); }}
              >
                <VesselGlyph vessel={type} size={20} />
              </button>
            ))}
          </div>
        </>
      )}
      {vesselOptions.length === 0 && (
        <p className="chemlab-context-menu-hint">{fullReason ?? 'This stand is full.'}</p>
      )}

      {/* No burner section at all on a stand no flame can reach — a row of
          permanently-dead buttons teaches nothing; the reason does. */}
      {canHeat ? (
        <>
          <div className="chemlab-context-menu-divider" />
          <p className="chemlab-context-menu-hint">Burner</p>
          <div className="chemlab-context-menu-vessels">
            {BURNER_TYPES.map(burner => (
              <button
                key={burner}
                className={`chemlab-context-menu-vessel${burner === burnerType ? ' active' : ''}`}
                title={burner === burnerType
                  ? `${BURNER_LABELS[burner]} — tap again to take it away`
                  : `${BURNER_LABELS[burner]} — ${BURNER_HINTS[burner]}`}
                onClick={() => { onChangeBurner(burner === burnerType ? 'none' : burner); onClose(); }}
              >
                <BurnerIcon burner={burner} size={20} />
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="chemlab-context-menu-divider" />
          <p className="chemlab-context-menu-hint">
            {standType === 'tube-rack'
              ? 'No flame near a wooden rack — clamp a tube on a stand to heat it.'
              : 'A burner stands on the bench; it can’t reach a vessel clamped above another.'}
          </p>
        </>
      )}
    </div>
  );
}
